import { useQuery } from 'react-query';

// Config.
import series from '@/config/series';

// Hooks.
import useFilters from './use-filters';

// Services.
import disambiguateFilters from '@/services/filters/disambiguate-filters';
import fetchTab from '@/services/data/fetch-tab';

/**
 * A custom hook for working with data from the API.
 * @returns {object} An object with data from the API and a loading flag.
 */
const useData = () => {
  const filters = useFilters();
  const { serviceType } = filters;

  // Set up all possible queries, but only fetch the all types data to start.
  const queries = {};
  for (const [slug] of series) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    queries[slug] = useQuery(slug, async () => fetchTab(series.get(slug)), { enabled: slug === 'allTypes' });
  }

  // For each requested service type in the filters, trigger a fetch.
  serviceType.forEach((seriesKey) => {
    const {
      isFetched,
      isFetching,
      isLoading,
      isSuccess,
      refetch,
    } = queries[seriesKey];
    if (!isFetched && !isFetching && !isLoading && !isSuccess) {
      refetch();
    }
  });

  // Determine if any of the queries are loading.
  const isLoading = Object.values(queries).reduce((acc, item) => acc || item.isLoading, false);

  // Create data object to be returned with info from the queries above.
  const data = {
    config: isLoading ? [] : queries.allTypes?.data?.config || [],
    isLoading,
    series: Object.keys(queries).reduce((acc, item) => {
      acc[item] = isLoading ? [] : queries[item].data?.data || [];

      return acc;
    }, {}),
  };

  // Compute the min/max year for the topline data.
  const [minYear, maxYear] = data.series.allTypes.reduce((acc, item) => {
    if (item.year < acc[0] || acc[0] === 0) {
      acc[0] = item.year;
    } else if (item.year > acc[1]) {
      acc[1] = item.year;
    }

    return acc;
  }, [0, 0]);
  data.minYear = minYear;
  data.maxYear = maxYear;

  // Ship disambiguated filters back with the data.
  data.disambiguatedFilters = disambiguateFilters(filters, minYear, maxYear);

  return data;
};

export default useData;
